import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';

interface SessionDetails {
  selectionId: string;
  teacherName: string;
  subject: string;
  classMode: string;
  scheduledAt: string;
  meetingLink?: string;
  meetingCode?: string;
}

export default function MeetingVerify() {
  const { encodedPayload } = useParams<{ encodedPayload: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [message, setMessage] = useState('');
  const [sessionDetails, setSessionDetails] = useState<SessionDetails | null>(null);

  useEffect(() => {
    const validateLink = async () => {
      if (!encodedPayload) {
        setMessage('Invalid meeting link');
        setLoading(false);
        return;
      }

      try {
        const response = await api.post('/teachers/selections/validate-meeting-link', {
          encodedPayload,
        });

        if (response.data.valid) {
          setValid(true);
          setMessage(response.data.message);
          setSessionDetails(response.data.sessionDetails);
        } else {
          setValid(false);
          setMessage(response.data.message);
        }
      } catch (error: any) {
        setValid(false);
        setMessage(error?.response?.data?.message || 'Failed to validate meeting link');
      } finally {
        setLoading(false);
      }
    };

    validateLink();
  }, [encodedPayload]);

  const formatSessionTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleCloseWindow = () => {
    window.close();
    setTimeout(() => {
      if (window.closed) return;
      if (window.history.length > 1) {
        navigate(-1);
        return;
      }
      navigate('/');
    }, 150);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Validating meeting link...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="text-center">
            {valid ? (
              <div className="space-y-4">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                  <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h1 className="text-2xl font-bold text-gray-900">Meeting Verified</h1>
                <p className="text-gray-600">{message}</p>
                
                {sessionDetails && (
                  <div className="mt-6 bg-gray-50 rounded-lg p-4 text-left">
                    <h2 className="text-lg font-semibold text-gray-900 mb-3">Session Details</h2>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Teacher</span>
                        <span className="font-medium">{sessionDetails.teacherName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Subject</span>
                        <span className="font-medium">{sessionDetails.subject}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Mode</span>
                        <span className="font-medium">{sessionDetails.classMode}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Scheduled</span>
                        <span className="font-medium">{formatSessionTime(sessionDetails.scheduledAt)}</span>
                      </div>
                      {sessionDetails.meetingCode && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Meeting Code</span>
                          <span className="font-medium">{sessionDetails.meetingCode}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="mt-6 space-y-2">
                  <button
                    onClick={() => navigate('/student/dashboard')}
                    className="w-full rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                  >
                    Go to Dashboard
                  </button>
                  <button
                    onClick={handleCloseWindow}
                    className="w-full rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-200"
                  >
                    Close Window
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h1 className="text-2xl font-bold text-gray-900">Invalid Link</h1>
                <p className="text-gray-600">{message}</p>
                
                <div className="mt-6 space-y-2">
                  <button
                    onClick={() => navigate('/student/dashboard')}
                    className="w-full rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                  >
                    Go to Dashboard
                  </button>
                  <button
                    onClick={handleCloseWindow}
                    className="w-full rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-200"
                  >
                    Close Window
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
